// The documents panel, shared by jobs, resources and the Library.
//
// One component rather than three near-identical ones, because the differences
// are genuinely small: what a job calls "Site photos", what a resource calls an
// expiry date, and who is allowed to press the buttons. Those are props.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api";
import {
  contentUrl,
  documents,
  uploadAttachment,
  type Attachment,
  type AttachmentCategory,
  type OwnerKind,
} from "../documentsApi";
import { useSession } from "../session";
import { folderOf, type DocumentFolder } from "../lib/attachmentFolders";
import { formatDMY } from "../lib/dates";
import { Banner } from "../ui";
import { CardList, RowCard } from "./ui/RowCard";
import { LinkedRow } from "./ui/LinkedRow";
import { SortHeader } from "./ui/SortHeader";
import { sortRows, useSort } from "../lib/sortRows";
import { DocumentViewer } from "./DocumentViewer";
import { DownloadLink } from "./DownloadLink";
import { FileDrop } from "./ui/FileDrop";

type DocSortKey = "title" | "size" | "expires" | "added";

interface Props {
  kind: OwnerKind;
  ownerId: string;
  /** Resource documents can carry an expiry date; nothing else can. */
  allowExpiry?: boolean;
  title?: string;
  /**
   * Draw the list in folders rather than as one run (round 31, machines).
   *
   * Groups inside the one panel, not tabs across the page: a machine's page is
   * meters and service history with documents at the bottom, and tabbing it
   * would hide the meters behind an Overview tab. An empty folder draws
   * nothing — a column of headings with "nothing here yet" under each is a
   * page telling you seven times that it is empty.
   */
  folders?: readonly DocumentFolder[];
  /**
   * Who may add, when the owner says so itself (round 44: a timesheet week's
   * `can.uploadFile`, which lets its own worker add a certificate). Absent
   * means the role rule below.
   */
  canUploadHere?: boolean;
  /** Overrides the standing hint under the upload box. */
  uploadHint?: string;
}

export function Documents({
  kind,
  ownerId,
  allowExpiry = false,
  title = "Documents",
  folders,
  canUploadHere,
  uploadHint,
}: Props) {
  const { user } = useSession();
  const [items, setItems] = useState<Attachment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await documents.list(kind, ownerId);
      setItems(result.attachments);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not load the documents.",
      );
    }
  }, [kind, ownerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isManager = user?.role === "manager" || user?.role === "admin";
  // A field worker may add photos to a job, and nothing else anywhere.
  const canUpload = canUploadHere ?? (isManager || kind === "job");
  // A timesheet takes evidence for a week: a PDF or a photo, from anybody
  // allowed to add one (round 44). Not the worker's photos-only job rule.
  const evidenceOnly = kind === "timesheet";

  async function remove(attachment: Attachment) {
    if (
      !window.confirm(
        `Remove “${attachment.title}”? An admin can get it back for 30 days.`,
      )
    ) {
      return;
    }
    try {
      await documents.remove(attachment.id);
      void load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not remove that.",
      );
    }
  }

  async function addSitePhotos(chosen: File[]) {
    if (chosen.length === 0) return;
    setPhotoBusy(true);
    setError(null);
    try {
      for (const file of chosen) {
        await uploadAttachment({ kind, ownerId, file, sitePhoto: true });
      }
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not upload that photo.",
      );
    } finally {
      setPhotoBusy(false);
    }
  }

  const files = items ?? [];
  const sitePhotos = files.filter((f) => f.isSitePhoto);
  const rest = files.filter((f) => !f.isSitePhoto);

  return (
    <>
      <section className="panel">
        <div className="row mb-3">
          <h2 className="!mb-0">{title}</h2>
        </div>

        {error && <Banner kind="error">{error}</Banner>}

        {canUpload && !(kind === "job" && !isManager) && (
          <UploadBox
            kind={kind}
            ownerId={ownerId}
            allowExpiry={allowExpiry && isManager}
            photosOnly={!isManager && !evidenceOnly}
            evidenceOnly={evidenceOnly}
            hint={uploadHint}
            folders={folders}
            onUploaded={() => void load()}
            onError={setError}
          />
        )}

        {items === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            {folders ? (
              // Every document has a folder to draw in, because null reads as
              // Miscellaneous and Miscellaneous is always in the list.
              rest.length === 0 ? (
                <p className="muted">Nothing attached yet.</p>
              ) : (
                folders.map((folder) => {
                  const inFolder = rest.filter(
                    (file) => folderOf(file.category) === folder.key,
                  );
                  if (inFolder.length === 0) return null;
                  return (
                    <div key={folder.key} className="mt-4 first:mt-0">
                      <h3 className="mb-2">
                        {folder.label}
                        <span className="muted"> ({inFolder.length})</span>
                      </h3>
                      <DocumentList
                        documents={inFolder}
                        emptyMessage=""
                        onOpen={setViewing}
                        onRemove={remove}
                        canRemove={() => isManager}
                        showExpiry={allowExpiry}
                      />
                    </div>
                  );
                })
              )
            ) : (
              <>
                <DocumentList
                  documents={rest}
                  emptyMessage="Nothing attached yet."
                  onOpen={setViewing}
                  onRemove={remove}
                  canRemove={() => isManager}
                  showExpiry={allowExpiry}
                />
              </>
            )}
          </>
        )}
      </section>

      {/* Site photos are their own panel on a job (round 45, Nathan: the two
          "look joined"). That is where the crew's evidence of what was
          actually on the ground lives, and it carries ITS OWN upload (round
          37, item 2): a manager's photo sent through the documents box would
          land among the documents, because "site photo" is an intent the
          server has to be told, not a file type it can guess. A field
          worker's photos-only box lives here too: everything they send is a
          site photo by construction. */}
      {kind === "job" && (
        <section className="panel">
          <h2>Site photos</h2>
          {isManager ? (
            <div className="upload-box">
              <FileDrop
                accept="image/*"
                multiple
                disabled={photoBusy}
                typeLabel="photos"
                label={photoBusy ? "Uploading…" : undefined}
                onFiles={(chosen) => void addSitePhotos(chosen)}
                onReject={setError}
              />
            </div>
          ) : (
            canUpload && (
              <UploadBox
                kind={kind}
                ownerId={ownerId}
                allowExpiry={false}
                photosOnly
                onUploaded={() => void load()}
                onError={setError}
              />
            )
          )}
          {items === null ? null : sitePhotos.length === 0 ? (
            <p className="muted">No photos from site yet.</p>
          ) : (
            <div className="photo-grid">
              {sitePhotos.map((photo) => (
                <figure key={photo.id}>
                  <button
                    className="photo-button"
                    onClick={() => setViewing(photo)}
                  >
                    <img
                      src={contentUrl(photo.id)}
                      alt={photo.title}
                      loading="lazy"
                    />
                  </button>
                  <figcaption>
                    <span className="muted">
                      {new Date(photo.uploadedAt).toLocaleDateString("en-AU")}
                    </span>
                    {(isManager || photo.uploadedById === user?.id) && (
                      <button
                        className="link"
                        onClick={() => void remove(photo)}
                      >
                        Remove
                      </button>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      )}

      {viewing && (
        <DocumentViewer attachment={viewing} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

export function DocumentList({
  documents: files,
  emptyMessage,
  onOpen,
  onRemove,
  canRemove,
  showExpiry = false,
}: {
  documents: Attachment[];
  emptyMessage: string;
  onOpen: (attachment: Attachment) => void;
  onRemove?: (attachment: Attachment) => void;
  canRemove?: (attachment: Attachment) => boolean;
  showExpiry?: boolean;
}) {
  // Hooks first, before the empty-list return — a hook after a conditional
  // return runs on some renders and not others, which React refuses.
  const { sortKey, sortDir, onSort } = useSort<DocSortKey>("added", "desc");

  const sorted = useMemo(
    () =>
      sortRows(
        files,
        (file) => {
          switch (sortKey) {
            case "title":
              return file.title;
            // A NUMBER, not the formatted "9.4 MB" string the cell shows —
            // sorted as text, 9.4 MB lands between 812 KB and 96 KB.
            case "size":
              return file.sizeBytes;
            case "expires":
              return file.expiresOn;
            case "added":
              return new Date(file.uploadedAt);
          }
        },
        sortDir,
      ),
    [files, sortKey, sortDir],
  );

  if (files.length === 0) return <p className="muted">{emptyMessage}</p>;

  const expiryPill = (file: Attachment) =>
    file.expiresOn ? (
      <span className={`pill expiry-${file.expiry} whitespace-nowrap`}>
        {file.expiry === "expired" ? "Expired " : ""}
        {formatDMY(file.expiresOn)}
      </span>
    ) : null;

  return (
    <>
      <CardList>
        {sorted.map((file) => (
          <RowCard
            key={file.id}
            title={
              // The title opens the viewer — the same 44px floor as every
              // other phone control, and left-aligned so a wrapped filename
              // reads as text, not as a centred button.
              <button
                type="button"
                className="inline-flex min-h-11 items-center text-left text-brand-800"
                onClick={() => onOpen(file)}
              >
                {file.title}
              </button>
            }
            badges={showExpiry ? expiryPill(file) : undefined}
            actions={
              <>
                <DownloadLink
                  href={contentUrl(file.id, { download: true })}
                  className="row-action"
                >
                  Download
                </DownloadLink>
                {onRemove && canRemove?.(file) && (
                  <button
                    type="button"
                    className="row-action-danger"
                    onClick={() => onRemove(file)}
                  >
                    Remove
                  </button>
                )}
              </>
            }
          >
            {file.description && <div>{file.description}</div>}
            <div>
              {formatSize(file.sizeBytes)} · added{" "}
              {new Date(file.uploadedAt).toLocaleDateString("en-AU")}
            </div>
          </RowCard>
        ))}
      </CardList>
      <div className="-mx-6 overflow-x-auto border-y border-gray-200 max-sm:hidden">
        <table className="data-table">
          <thead>
            <tr>
              <SortHeader
                label="Title"
                sortKey="title"
                current={sortKey}
                dir={sortDir}
                onSort={onSort}
              />
              <SortHeader
                label="Size"
                sortKey="size"
                current={sortKey}
                dir={sortDir}
                onSort={onSort}
                className="table-num"
              />
              {showExpiry && (
                <SortHeader
                  label="Expires"
                  sortKey="expires"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                />
              )}
              <SortHeader
                label="Added"
                sortKey="added"
                current={sortKey}
                dir={sortDir}
                onSort={onSort}
              />
              <th className="w-px" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((file) => (
              <LinkedRow key={file.id} onOpen={() => onOpen(file)}>
                <td>
                  <button
                    type="button"
                    className="table-link"
                    onClick={() => onOpen(file)}
                  >
                    {file.title}
                  </button>
                  {file.description && (
                    <div className="mt-0.5 text-xs text-gray-600">
                      {file.description}
                    </div>
                  )}
                </td>
                {/* File sizes read down the column, so they are tabular and right
                aligned — "9.4 MB" under "812 KB" only compares if the digits
                line up. */}
                <td className="table-muted table-num whitespace-nowrap">
                  {formatSize(file.sizeBytes)}
                </td>
                {showExpiry && (
                  <td>
                    {file.expiresOn ? (
                      <span
                        className={`pill expiry-${file.expiry} whitespace-nowrap`}
                      >
                        {file.expiry === "expired" ? "Expired " : ""}
                        {formatDMY(file.expiresOn)}
                      </span>
                    ) : (
                      <span className="table-muted">—</span>
                    )}
                  </td>
                )}
                <td className="table-muted whitespace-nowrap">
                  {new Date(file.uploadedAt).toLocaleDateString("en-AU")}
                </td>
                <td className="whitespace-nowrap text-right">
                  <DownloadLink
                    href={contentUrl(file.id, { download: true })}
                    className="row-action"
                  >
                    Download
                  </DownloadLink>
                  {onRemove && canRemove?.(file) && (
                    <button
                      type="button"
                      className="row-action-danger ml-1"
                      onClick={() => onRemove(file)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </LinkedRow>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function UploadBox({
  kind,
  ownerId,
  allowExpiry,
  photosOnly,
  category,
  folders,
  serviceRecordId,
  hint,
  evidenceOnly = false,
  onUploaded,
  onError,
}: {
  kind: OwnerKind;
  ownerId: string;
  allowExpiry: boolean;
  photosOnly: boolean;
  /** The folder this box always files into — a person's tab (round 27). */
  category?: AttachmentCategory;
  /** Or: let the person CHOOSE the folder, from these (round 31, machines). */
  folders?: readonly DocumentFolder[];
  /** Files it against a service record, which fixes the folder. */
  serviceRecordId?: string;
  /** Overrides the standing hint under the box. */
  hint?: string;
  /** PDF and images only (round 44, a timesheet's certificates). */
  evidenceOnly?: boolean;
  onUploaded: () => void;
  onError: (message: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  // Miscellaneous by default. The alternative — no default, and a required
  // pick — turns "attach this before I forget" into a decision, and the
  // document that never gets uploaded is worse than the one in the wrong
  // folder. Moving it later is a PATCH away.
  const [folder, setFolder] = useState<AttachmentCategory>("miscellaneous");
  const chosen = folders ? folder : category;
  const chosenFolder = folders?.find((f) => f.key === folder);
  // The element ids were the owner's id alone, which was unique while a page
  // had one upload box. An asset page now has two at once — the documents
  // panel and an open "Attach invoice" on a service record — and two inputs
  // sharing an id means the second box's label focuses the FIRST box's file
  // picker. Duplicate ids are also invalid HTML and confuse a screen reader
  // about which control a label names.
  const field = `${ownerId}-${serviceRecordId ?? category ?? "main"}`;

  async function send(files: File[]) {
    if (files.length === 0) return;

    for (const file of files) {
      setProgress(0);
      try {
        await uploadAttachment(
          {
            kind,
            ownerId,
            file,
            // A title only makes sense for a single file; a batch keeps its
            // own filenames.
            ...(files.length === 1 && title !== "" ? { title } : {}),
            ...(allowExpiry && expiresOn !== "" ? { expiresOn } : {}),
            ...(chosen ? { category: chosen } : {}),
            ...(serviceRecordId ? { serviceRecordId } : {}),
          },
          setProgress,
        );
      } catch (err) {
        onError(
          err instanceof ApiError
            ? err.message
            : `Could not upload ${file.name}.`,
        );
      } finally {
        setProgress(null);
      }
    }

    setTitle("");
    setExpiresOn("");
    onUploaded();
  }

  const hasFields = !photosOnly || !!folders || allowExpiry;

  return (
    <div className="upload-box">
      {hasFields && (
        <div className="row">
          {!photosOnly && (
            <label htmlFor={`title-${field}`} style={{ flex: "1 1 14rem" }}>
              Title (optional)
              <input
                id={`title-${field}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Defaults to the filename"
              />
            </label>
          )}

          {folders && (
            <label htmlFor={`folder-${field}`} style={{ flex: "0 1 14rem" }}>
              Folder
              <select
                id={`folder-${field}`}
                value={folder}
                onChange={(e) =>
                  setFolder(e.target.value as AttachmentCategory)
                }
              >
                {folders.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {allowExpiry && (
            <label htmlFor={`expires-${field}`} style={{ flex: "0 1 12rem" }}>
              Expires (optional)
              <input
                id={`expires-${field}`}
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
              />
            </label>
          )}
        </div>
      )}

      {/* Round 45: every picker takes a drop as well as a browse, through the
          one FileDrop (round 33's hidden input lives inside it). */}
      <div className={hasFields ? "mt-3" : ""}>
        <FileDrop
          id={`file-${field}`}
          multiple={!photosOnly}
          disabled={progress !== null}
          accept={
            photosOnly
              ? "image/*"
              : evidenceOnly
                ? ".pdf,image/*"
                : ".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx"
          }
          typeLabel={
            photosOnly
              ? "photos"
              : evidenceOnly
                ? "PDF or image"
                : "PDF, images, Word and Excel"
          }
          label={photosOnly ? "Add a photo" : undefined}
          onFiles={(files) => void send(files)}
          onReject={onError}
        />
      </div>

      {progress !== null && (
        <p className="hint" role="status">
          Uploading… {Math.round(progress * 100)}%
        </p>
      )}

      <p className="hint">
        {hint ??
          chosenFolder?.hint ??
          (photosOnly
            ? "You can add photos to jobs you are booked on. You can remove your own photo for 15 minutes."
            : "PDF, images, Word and Excel. PDFs and images open in the app; Word and Excel download.")}
      </p>
    </div>
  );
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
